import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { setContextTriggerFunction } from '../utils/UsageUtils';

const UsageRefreshContext = createContext();

export const UsageRefreshProvider = ({ children }) => {
  const [refreshCallbacks, setRefreshCallbacks] = useState(new Set());

  const registerRefreshCallback = useCallback(callback => {
    if (typeof callback !== 'function') {
      console.error('Usage refresh callback must be a function');
      return () => {};
    }

    setRefreshCallbacks(prev => {
      const newCallbacks = new Set(prev);
      newCallbacks.add(callback);
      return newCallbacks;
    });

    return () => {
      setRefreshCallbacks(prev => {
        const newCallbacks = new Set(prev);
        newCallbacks.delete(callback);
        return newCallbacks;
      });
    };
  }, []);

  const triggerRefresh = useCallback(
    (immediate = false) => {
      if (refreshCallbacks.size === 0) {
        console.warn('No usage refresh callbacks registered');
        return;
      }

      const executeCallbacks = () => {
        refreshCallbacks.forEach(callback => {
          try {
            callback();
          } catch (error) {
            console.error('Error in usage refresh callback:', error);
          }
        });
      };

      if (immediate) {
        executeCallbacks();
      } else {
        setTimeout(executeCallbacks, 1000);
      }
    },
    [refreshCallbacks]
  );

  const triggerImmediateRefresh = useCallback(() => {
    triggerRefresh(true);
  }, [triggerRefresh]);

  const triggerDelayedRefresh = useCallback(() => {
    triggerRefresh(false);
  }, [triggerRefresh]);

  useEffect(() => {
    setContextTriggerFunction(triggerRefresh);

    return () => {
      setContextTriggerFunction(null);
    };
  }, [triggerRefresh]);

  const value = {
    registerRefreshCallback,
    triggerRefresh,
    triggerImmediateRefresh,
    triggerDelayedRefresh,
  };

  return <UsageRefreshContext.Provider value={value}>{children}</UsageRefreshContext.Provider>;
};

export const useUsageRefresh = () => {
  const context = useContext(UsageRefreshContext);
  if (!context) {
    throw new Error('useUsageRefresh must be used within a UsageRefreshProvider');
  }
  return context;
};

export const useRegisterRefreshCallback = callback => {
  const { registerRefreshCallback } = useUsageRefresh();

  React.useEffect(() => {
    const unregister = registerRefreshCallback(callback);
    return unregister;
  }, [callback, registerRefreshCallback]);
};
